import { FC, useState, useRef } from "react";
import Modal from "react-modal";
import close from "assets/images/close.svg";
import Imgclose from "assets/images/input-img-close-icon.svg";
import { useSelector } from "react-redux";
import { dispatch } from "store";
import { setBulkIngredientDetailsModalIsClosed } from "store/user-slice";
import Button from "components/particles/primary-button";
import csvIcon from "assets/images/csv.svg";
import zipIcon from "assets/images/zipfile.svg";
import "./style.css";
import { useBulkIngredientDetails } from "./useHook";
import Swal from "sweetalert2";

const BulkIngredientDetailsPopup: FC<{ getIngredients: Function }> = ({ getIngredients }) => {
    const { bulkIngredientDetailsModalIsOpen } = useSelector((state: any) => state.user);
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [zipFile, setZipFile] = useState<File | null>(null);
    const [error, setError] = useState<string>("");
    const [zipError, setZipError] = useState<string>("");
    const [csvFileLink, setCsvFileLink] = useState<string>("");
    const [startRange, setStartRange] = useState<string>("");
    const [endRange, setEndRange] = useState<string>("");
    const [rangeError, setRangeError] = useState<string>("");
    const csvInputRef = useRef<HTMLInputElement>(null);
    const zipInputRef = useRef<HTMLInputElement>(null);
    const { downloadSampleCsvFile, uploadBulkIngredientDetails } = useBulkIngredientDetails();


    function closeModal() {
        dispatch(setBulkIngredientDetailsModalIsClosed());
        setCsvFile(null);
        setZipFile(null);
        setError("");
        setZipError("");
        setStartRange("");
        setEndRange("");
        setRangeError("");
    }

    const validateRangeInput = (value: string, type: 'start' | 'end') => {
        const numericValue = value.replace(/[^0-9]/g, '');

        if (numericValue === '') {
            return numericValue;
        }

        const numValue = parseInt(numericValue);

        if (numValue <= 0) {
            return '';
        }

        return numericValue;
    };

    const handleStartRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const validatedValue = validateRangeInput(value, 'start');
        setStartRange(validatedValue);

        if (rangeError) {
            setRangeError("");
        }

        if (validatedValue && endRange) {
            const startNum = parseInt(validatedValue);
            const endNum = parseInt(endRange);
            if (startNum > endNum) {
                setRangeError("Start range cannot be greater than end range");
            } else {
                setRangeError("");
            }
        }
    };

    const handleEndRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const validatedValue = validateRangeInput(value, 'end');
        setEndRange(validatedValue);

        if (rangeError) {
            setRangeError("");
        }

        if (startRange && validatedValue) {
            const startNum = parseInt(startRange);
            const endNum = parseInt(validatedValue);
            if (startNum > endNum) {
                setRangeError("Start range cannot be greater than end range");
            } else {
                setRangeError("");
            }
        }
    };

    const handleCsvClick = () => {
        csvInputRef.current?.click();
    };

    const handleCsvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const fileExtension = file.name.split('.').pop()?.toLowerCase();

            if (fileExtension !== 'csv' && fileExtension !== 'xlsx') {
                setError("Please select a CSV or Excel (.xlsx) file");
                e.target.value = '';
                return;
            }

            const isValidCsv = file.type.match('text/csv') ||
                file.type.match('application/csv') ||
                file.type.match('text/x-csv') ||
                file.type.match('application/x-csv') ||
                file.type.match('text/comma-separated-values') ||
                file.type.match('text/x-comma-separated-values');

            const isValidExcel = file.type.match('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
                file.type.match('application/vnd.ms-excel') ||
                file.type === '';

            if (!isValidCsv && !isValidExcel && fileExtension !== 'xlsx' && fileExtension !== 'csv') {
                setError("Please select a valid CSV or Excel (.xlsx) file");
                e.target.value = '';
                return;
            }

            setCsvFile(file);
            setError("");
        }
    };

    const getErrorMessageFromError = async (error: any): Promise<string> => {
        const data = error?.response?.data;
        if (data?.message && typeof data.message === 'string') {
            return data.message;
        }
        if (data instanceof Blob) {
            try {
                const text = await data.text();
                const parsed = JSON.parse(text);
                if (parsed?.message) return parsed.message;
            } catch {
                // ignore
            }
        }
        return 'Something went wrong. Please try again.';
    };

    const handleDownloadSampleCsvFile = async () => {
        if (startRange && endRange) {
            const startNum = parseInt(startRange);
            const endNum = parseInt(endRange);
            if (startNum > endNum) {
                setRangeError("Start range cannot be greater than end range");
                return;
            }
        }

        const start = startRange ? parseInt(startRange) : undefined;
        const end = endRange ? parseInt(endRange) : undefined;
        try {
            await downloadSampleCsvFile(setCsvFileLink, start, end);
            setStartRange("");
            setEndRange("");
            setRangeError("");
        } catch (error: any) {
            const errorMessage = await getErrorMessageFromError(error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: errorMessage,
            });
        }
    }

    const handleSubmit = async () => {
        if (!csvFile) {
            setError("CSV or Excel file is required");
            return;
        }

        const formData = new FormData();
        formData.append("csvFile", csvFile);

        if (zipFile) {
            formData.append("imageZipFile", zipFile);
        }

        try {
            const response = await uploadBulkIngredientDetails(formData);
            if (response?.status === 200) {
                Swal.fire({
                    title: 'Success!',
                    text: 'Bulk ingredient details added successfully',
                    icon: 'success',
                });
                closeModal();
                getIngredients();
            }
        } catch (error: any) {
            const errorMessage = await getErrorMessageFromError(error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: errorMessage,
            });
            setError(errorMessage);
        }
    };

    const handleRemoveFile = () => {
        setCsvFile(null);
        if (csvInputRef.current) {
            csvInputRef.current.value = '';
        }
    };

    const handleZipClick = () => {
        zipInputRef.current?.click();
    };

    const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const fileExtension = file.name.split('.').pop()?.toLowerCase();

            if (fileExtension !== 'zip') {
                setZipError("Please select a ZIP file");
                e.target.value = '';
                return;
            }

            const isValidZip = file.type.match('application/zip') ||
                file.type.match('application/x-zip-compressed') ||
                file.type === '';

            if (!isValidZip && fileExtension !== 'zip') {
                setZipError("Please select a valid ZIP file");
                e.target.value = '';
                return;
            }

            setZipFile(file);
            setZipError("");
        }
    };

    const handleRemoveZipFile = () => {
        setZipFile(null);
        if (zipInputRef.current) {
            zipInputRef.current.value = '';
        }
    };

    return (
        <Modal
            isOpen={bulkIngredientDetailsModalIsOpen}
            className="styled-modal"
            onRequestClose={closeModal}
            contentLabel="Bulk Ingredient Details Modal"
        >
            <div className="bulk-ingredient-details-popup">
                <div className="close-button" onClick={closeModal}>
                    <img src={close} style={{ cursor: "pointer" }} alt="close" />
                </div>
                <div className="bulk-ingredient-details-popup-header">
                    <h1>Bulk Ingredient Details</h1>
                </div>
                <div className="range-inputs-container">
                    <div className="range-input-group">
                        <label htmlFor="startRange">Start Range (Optional)</label>
                        <input
                            id="startRange"
                            type="text"
                            placeholder="Enter start range (min: 1)"
                            value={startRange}
                            onChange={handleStartRangeChange}
                        />
                    </div>
                    <div className="range-input-group">
                        <label htmlFor="endRange">End Range (Optional)</label>
                        <input
                            id="endRange"
                            type="text"
                            placeholder="Enter end range (min: 1)"
                            value={endRange}
                            onChange={handleEndRangeChange}
                        />
                    </div>
                    {rangeError && <div className="range-error-message">{rangeError}</div>}
                    <div className="range-actions-container">
                        <p className="range-help-text">Leave empty to include all records</p>
                        <Button
                            title="Generate Excel"
                            width="16.5rem"
                            fill="true"
                            backgroundcolor={rangeError ? "#ccc" : "var(--reset-button)"}
                            onclick={rangeError ? () => {
                            } : handleDownloadSampleCsvFile}
                            disabled={!!rangeError}
                        />
                    </div>
                </div>
                <div className="bulk-ingredient-details-popup-content">
                    <div className="bulk-ingredient-details-popup-content-option">
                        <h2>Upload CSV or Excel file <span className="required">*</span></h2>
                        <input
                            ref={csvInputRef}
                            type="file"
                            accept=".csv,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/csv,text/x-csv,application/x-csv,text/comma-separated-values,text/x-comma-separated-values"
                            onChange={handleCsvChange}
                            required
                            style={{ display: 'none' }}
                        />
                        <div className="file-input-container">
                            <button className="file-input-button" onClick={handleCsvClick}>
                                <img src={csvIcon} alt="CSV/Excel" />
                                <div className="file-name-container">
                                    <span>{csvFile ? csvFile.name : 'Select CSV or Excel File'}</span>
                                    {csvFile && (
                                        <img
                                            className="close-icon"
                                            src={Imgclose}
                                            alt="Remove"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveFile();
                                            }}
                                            title="Remove file"
                                        />
                                    )}
                                </div>
                            </button>
                        </div>
                        <p>Please upload a CSV or Excel (.xlsx) file with the following columns: Ingredient Id, name, price, quantity, unit, description</p>
                    </div>
                    {error && <div className="error-message">{error}</div>}
                    <div className="bulk-ingredient-details-popup-content-option">
                        <h2>Upload Image ZIP file <span className="optional"></span></h2>
                        <input
                            ref={zipInputRef}
                            type="file"
                            accept=".zip,application/zip,application/x-zip-compressed"
                            onChange={handleZipChange}
                            style={{ display: 'none' }}
                        />
                        <div className="file-input-container">
                            <button className="file-input-button" onClick={handleZipClick}>
                                <img src={zipIcon} alt="ZIP" />
                                <div className="file-name-container">
                                    <span>{zipFile ? zipFile.name : 'Select ZIP File'}</span>
                                    {zipFile && (
                                        <img
                                            className="close-icon"
                                            src={Imgclose}
                                            alt="Remove"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveZipFile();
                                            }}
                                            title="Remove file"
                                        />
                                    )}
                                </div>
                            </button>
                        </div>
                        <p>Upload a ZIP file containing images. Image filenames should match those in the Image column of the CSV/Excel file.</p>
                    </div>
                    {zipError && <div className="error-message">{zipError}</div>}
                    <div className="submit-button-container">
                        <Button
                            title="Upload File"
                            width="16.5rem"
                            fill="true"
                            backgroundcolor={csvFile ? "var(--reset-button)" : "#ccc"}
                            onclick={csvFile ? handleSubmit : () => {
                                setError("Please select a CSV or Excel file");
                            }}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default BulkIngredientDetailsPopup;